import {Injectable} from "@angular/core";
import * as _ from "lodash";
import {
  AM_DEFAULTS, AM_LOCATIONS, AM_MARITAL_STATUS, AM_SPECIAL_PROFESSIONS, CHILD_CARE_SOURCE_LINKS,
  STATE_SOI_CODES
} from "../../src/app/shared/map.defaults";
import {Observable} from "rxjs";
import {Http} from "@angular/http";
import {TransferState} from "../../src/modules/transfer-state/transfer-state";
import {TaxesApi} from "../../src/app/shared/sdk/services/custom/Taxes";
import {CostOfLivingUtils} from "./costoflivingutils";
import {TaxesUtils} from "./taxesutils";

@Injectable()
export class AffordabilityUtils {

  private annualWages: Observable<any>;
  private taxesIndicators: any = {};
  private foodCosts: any;
  private utilitiesCosts: any;
  private childCareCosts: any;
  private healthCareCosts: any;
  private transportationCosts: any;

  constructor(protected http: Http,
              protected cache: TransferState,
              protected costOfLivingUtils: CostOfLivingUtils,
              protected taxesUtils: TaxesUtils) {}

  private getThousandIndex(amount: number) {

    let result: number = 0;

    if (amount) {
      result = Math.floor(amount / 1000);
      if (amount % 1000 >= 500) {
        result ++;
      }
    }

    return result;
  }

  private getIncomeIndex(income) {

    let index:number = 0;
    let range = AM_DEFAULTS.incomeRange;

    for (var i = 0; i <= range.length; i ++) {

      index = i;

      if (income < range[i]) {
        break;
      }
    }

    return index;
  }

  private getIncomeKey(firstIncome, secondIncome) {

    let result: string = '';

    if (firstIncome > secondIncome) {
      result = this.getThousandIndex(firstIncome) + '_' + this.getThousandIndex(secondIncome);
    } else {
      result = this.getThousandIndex(secondIncome) + '_' + this.getThousandIndex(firstIncome);
    }

    return result;
  }

  private getStatesAndIncomeKeys(profile, features) {

    let result: any = {};

    for (let feature of features) {

      let state = feature.properties.state.digit_code;
      let firstIncome: number;
      let secondIncome: number;
      let incomeKey: string;

      if (profile.firstProfession.code == AM_SPECIAL_PROFESSIONS.FIRST_CUSTOM_INCOME.code) {
        firstIncome = profile.firstProfessionIncome;
      } else {
        firstIncome = this.calculateIncome(profile.firstProfession, profile, feature);
      }

      if (profile.householdMembers.length == 1) {
        secondIncome = 0;
      } else if (profile.secondProfession.code == AM_SPECIAL_PROFESSIONS.SECOND_CUSTOM_INCOME.code) {
        secondIncome = profile.secondProfessionIncome;
      } else {
        secondIncome = this.calculateIncome(profile.secondProfession, profile, feature);
      }

      incomeKey = this.getIncomeKey(firstIncome, secondIncome);

      if (!result[state]) {
        result[state] = [];
      }

      if (result[state].indexOf(incomeKey) == -1) {
        result[state].push(incomeKey);
      }
    }

    return result
  }

  private getMaritalStatus(profile) {

    if (profile.secondProfession.code != AM_SPECIAL_PROFESSIONS.NO_SPOUSE.code && profile.householdMembers.length > 1) {
      return AM_MARITAL_STATUS.MARRIED;
    } else if (profile.householdMembers.length == 1 || this.getNumberOfChildren(profile) == 0) {
      return AM_MARITAL_STATUS.SINGLE;
    } else {
      return AM_MARITAL_STATUS.HEAD_OF_HOUSEHOLD;
    }
  }

  private getDependentExemptions(profile) {

    if (profile.householdMembers.length == 1) {
      return 0;
    } else if (profile.secondProfession.code == AM_SPECIAL_PROFESSIONS.STAY_AT_HOME.code
      || profile.secondProfession.code == AM_SPECIAL_PROFESSIONS.NO_SPOUSE.code) {
      return profile.householdMembers.length - 1;
    } else {
      return profile.householdMembers.length - 2;
    }
  }

  private getNumberOfChildren(profile) {

    let total: number = 0;

    for (let member of profile.householdMembers) {
      if (member.age <= AM_DEFAULTS.maxAgeForChildren) {
        total ++;
      }
    }

    return total;
  }

  public getNumberOfAdults(profile) {

    let total: number = 0;

    for (let member of profile.householdMembers) {
      if (member.age > AM_DEFAULTS.maxAgeForChildren) {
        total ++;
      }
    }

    return total;
  }

  private getIncomeByCode(professionCode, locationCode) {

    let annualWage: any = null;
    let locationIndex: number = _.indexOf(AM_LOCATIONS, locationCode);

    if (locationIndex !== -1) {
      annualWage = this.annualWages[professionCode][locationIndex] !== 0
        ? this.annualWages[professionCode][locationIndex] : null;
    }

    return annualWage;
  }

  private calculateIncome(profession, profile, feature) {

    let annualWage: number = 0;
    let cbsa: any = feature.properties.cbsa;
    let state: any = feature.properties.state.digit_code;

    if (profession.code === AM_SPECIAL_PROFESSIONS.NO_SPOUSE.code
      || profession.code === AM_SPECIAL_PROFESSIONS.STAY_AT_HOME.code) {

      annualWage = 0;

    } else if (profession.code === AM_SPECIAL_PROFESSIONS.AVERAGE_INCOME.code) {

      annualWage = AM_DEFAULTS.usaAverageAmount;

    } else if (profession.code === AM_SPECIAL_PROFESSIONS.MINIMUM_WAGE.code) {

      annualWage = AM_DEFAULTS.minimumWageAmount;

    } else if (profession.code === AM_SPECIAL_PROFESSIONS.FIRST_CUSTOM_INCOME.code) {

      annualWage = Math.round(profile.firstProfessionIncome);

    } else if (profession.code === AM_SPECIAL_PROFESSIONS.SECOND_CUSTOM_INCOME.code) {

      annualWage = Math.round(profile.secondProfessionIncome);

    } else {

      var income = this.getIncomeByCode(profession.code, cbsa);

      if (!income) {
        income = this.getIncomeByCode(profession.code, state);
      }

      if (!income) {
        income = this.getIncomeByCode(profession.code, AM_DEFAULTS.usaRegionCode);
      }

      if (!!income) {
        annualWage = income * AM_DEFAULTS.inflationFactor15_16;
      }
    }

    return Math.ceil(annualWage);
  }

  private getIncomeSourceLink(profile) {

    let urlPath: string = null;
    let profession: any = profile.firstProfession;

    if (profession != AM_SPECIAL_PROFESSIONS.FIRST_CUSTOM_INCOME.code
      && profession != AM_SPECIAL_PROFESSIONS.MINIMUM_WAGE.code
      && profession != AM_SPECIAL_PROFESSIONS.AVERAGE_INCOME.code) {

      urlPath = 'http://www.bls.gov/oes/current/oes' + profession.code.replace('-', '') + '.htm';
    }

    return urlPath;
  }

  private calculateTaxes(profile, firstIncome, secondIncome, feature) {

    let result: number = 0;
    let state: any = feature.properties.state.digit_code;
    let incomeKey: string = this.getIncomeKey(firstIncome, secondIncome);
    let cacheKey = "taxes:" + state + ":" + incomeKey;
    let taxesData = this.cache.get(cacheKey);

    if (taxesData) {

      let profileCode = [
        this.getMaritalStatus(profile),
        this.getDependentExemptions(profile),
        this.getNumberOfChildren(profile),
        this.calculateChildCareCosts(profile, feature, false)].join('-');

      for (let indicator of taxesData.taxes) {

        if (indicator.profile_regexp.slice(-1) != ')') {
          indicator.profile_regexp = indicator.profile_regexp + ')';
        }

        let regExp = new RegExp(indicator.profile_regexp, "i");

        if (profileCode.match(regExp)) {
          result = indicator.value;
          break;
        }
      }
    }

    return result;
  }

  private getTaxesSourceLink(profile, firstIncome, secondIncome, feature) {

    let urlPath: string = '/maps/taxsim?taxYear=2013';
    let maritalStatus: any = this.getMaritalStatus(profile);
    urlPath += '&maritalStatus=' + maritalStatus;

    let dependentExemptions = this.getDependentExemptions(profile);
    if (dependentExemptions > 0) {
      urlPath += '&dependentExemptions=' + dependentExemptions;
    }

    let childrenNumber = this.getNumberOfChildren(profile);
    if (childrenNumber > 0) {
      urlPath += '&childrenNumber=' + childrenNumber;

      if (!!feature.properties.childCareCosts && feature.properties.childCareCosts > 0) {
        urlPath += '&childCareExpenses=' + feature.properties.childCareCosts;
      }
    }

    if (!!firstIncome && firstIncome > 0) {
      urlPath += '&firstIncome=' + firstIncome;
    }

    if (!!secondIncome && secondIncome > 0) {
      urlPath += '&secondIncome=' + secondIncome;
    }

    if (!!feature.properties.state.code) {
      urlPath += '&stateCode=' + STATE_SOI_CODES[feature.properties.state.code];
    }

    return urlPath;
  }

  private calculateRentalPrice(profile, feature) {

    let result: number = 0;

    if (feature.properties.rental_price) {

      let rentalPrice: number = parseFloat(feature.properties.rental_price);
      result = rentalPrice * profile.houseRentArea * 12;
    }

    return Math.ceil(result);
  }

  private getRentalSourceLink(feature) {

    let cbsaName: string = '';

    if (!!feature.properties.cbsa_rh) {
      cbsaName = feature.properties.cbsa_rh;
    } else {
      let city: string = feature.properties.name;
      let state: string = feature.properties.state.code;
      cbsaName = city + ', ' + state;
    }

    return encodeURI('http://renthub.com/maps/cbsa_census_tract/' + cbsaName);
  }

  private calculateCostOfFood(profile) {

    let result: number = 0;
    let foodPlanCosts: any = this.foodCosts[profile.foodPlan.code];

    for (let member of profile.householdMembers) {
      let key = member.gender + member.age;
      result += foodPlanCosts[key];
    }

    return result;
  }

  private getCostOfFoodSourceLink() {
    return 'http://www.cnpp.usda.gov/USDAFoodPlansCostofFood/reports';
  }

  private getConsumerCostsSourceLink() {
    return '/maps/bls-cex';
  }

  private calculateUtilitiesCosts(income, profile) {

    let incomeIndex = this.getIncomeIndex(income);
    let totalPersons = profile.householdMembers.length;

    return this.utilitiesCosts[totalPersons][incomeIndex] / 100 * income * AM_DEFAULTS.inflationFactor14_16;
  }

  private calculateChildCareCosts(profile, feature, applyInflation) {

    let cost: number = 0;
    let state: any = feature.properties.state.digit_code;

    if (profile.secondProfession.code != AM_SPECIAL_PROFESSIONS.STAY_AT_HOME.code) {

      for (let member of profile.householdMembers) {

        if (member.age < AM_DEFAULTS.maxAgeForChildren) {

          if (member.age < 4) {
            cost += this.childCareCosts[state][0];
          } else if (member.age == 4) {
            cost += this.childCareCosts[state][1];
          } else {
            cost += this.childCareCosts[state][2];
          }
        }
      }
    }

    return applyInflation ? Math.ceil(cost * AM_DEFAULTS.inflationFactor14_16) : cost;
  }

  private getChildCareCostsSourceLink(feature) {
    return CHILD_CARE_SOURCE_LINKS.baseURL + CHILD_CARE_SOURCE_LINKS.statePath[feature.properties.state.code];
  }

  private calculateTransportationCosts(income, profile) {

    let incomeIndex: number = this.getIncomeIndex(income);
    let totalPersons: number = profile.householdMembers.length;

    return this.transportationCosts[totalPersons][incomeIndex] / 100 * income * AM_DEFAULTS.inflationFactor14_16;
  }

  private calculateHealthCareCosts(income, profile) {

    let incomeIndex: number = this.getIncomeIndex(income);
    let totalPersons: number = profile.householdMembers.length;

    return this.healthCareCosts[totalPersons][incomeIndex] / 100 * income * AM_DEFAULTS.inflationFactor14_16;
  }

  private calculateIndicators(profile, feature) {

    //Incomes
    let firstIncome: number = this.calculateIncome(profile.firstProfession, profile, feature);
    let secondIncome: number = profile.householdMembers.length > 1 ?
      this.calculateIncome(profile.secondProfession, profile, feature) : 0;
    let income: number = firstIncome + secondIncome;
    feature.properties.income = income;
    feature.properties.incomeSourceLink = this.getIncomeSourceLink(profile);
    feature.properties.taxes = this.calculateTaxes(profile, firstIncome, secondIncome, feature);
    feature.properties.taxesSourceLink = this.getTaxesSourceLink(profile, firstIncome, secondIncome, feature);
    feature.properties.houseRentCosts = this.calculateRentalPrice(profile, feature);
    feature.properties.houseRentSourceLink = this.getRentalSourceLink(feature);
    feature.properties.foodCosts = this.calculateCostOfFood(profile);
    feature.properties.foodCostsSourceLink = this.getCostOfFoodSourceLink();
    feature.properties.utilitiesCosts = this.calculateUtilitiesCosts(income, profile);
    feature.properties.utilitiesCostsSourceLink = this.getConsumerCostsSourceLink();
    feature.properties.childCareCosts = this.calculateChildCareCosts(profile, feature, true);
    feature.properties.childCareCostsSourceLink = this.getChildCareCostsSourceLink(feature);
    feature.properties.healthCareCosts = this.calculateHealthCareCosts(income, profile);
    feature.properties.healthCareCostsSourceLink = this.getConsumerCostsSourceLink();
    feature.properties.transportationCosts = this.calculateTransportationCosts(income, profile);
    feature.properties.transportationCostsSourceLink = this.getConsumerCostsSourceLink();
    feature.properties.expenses = feature.properties.taxes + feature.properties.houseRentCosts
      + feature.properties.foodCosts + feature.properties.utilitiesCosts + feature.properties.childCareCosts
      + feature.properties.healthCareCosts + feature.properties.transportationCosts;

    feature.properties.moneyLeft = feature.properties.income - feature.properties.expenses;

    return feature;
  }

  public findProfession(professions, code) {

    let profession: any;
    let categoryCode: string = code.substring(0, 2);

    if (categoryCode === '00') {
      profession = _.find(AM_SPECIAL_PROFESSIONS, function (o) {
        return o.code === code;
      });

    } else {

      let category: any = _.find(professions, function (o) {
        return o.code === categoryCode;
      });

      if (!!category) {
        profession = _.find(category.professions, function (o) {
          return o.code === code;
        });
      }
    }

    return profession;
  }

  public loadIndicators() {
    return Observable.zip(this.loadAnnualWageIndicators(), this.loadCostsOfLiving());
  }

  protected loadCostsOfLiving(): Observable<any> {

    let cacheKey = "costsOfLiving";
    let costsOfLiving = this.cache.get(cacheKey);

    if (!costsOfLiving) {
      return this.costOfLivingUtils.loadBCostsOfLiving()
        .map((data: any) => {
          this.cache.set(cacheKey, data);
          this.foodCosts = data.food;
          this.utilitiesCosts = data.utilities;
          this.childCareCosts = data.childCare;
          this.healthCareCosts = data.healthCare;
          this.transportationCosts = data.transportation;
        });
    } else {
      this.foodCosts = costsOfLiving.food;
      this.utilitiesCosts = costsOfLiving.utilities;
      this.childCareCosts = costsOfLiving.childCare;
      this.healthCareCosts = costsOfLiving.healthCare;
      this.transportationCosts = costsOfLiving.transportation;
      return Observable.of(costsOfLiving);
    }
  }

  protected loadAnnualWageIndicators(): Observable<any> {

    let cacheKey = "annualWages";
    let annualWages = this.cache.get(cacheKey);

    if (!annualWages) {
      return this.http.get("https://s3.amazonaws.com/test.fixr.com/maps/annual-wages/2015/annual_wages.json")
        .map((response) => {
          let data = response.json();
          this.cache.set(cacheKey, data);
          this.annualWages = data;
        });
    } else {
      this.annualWages = annualWages;
      return Observable.of(annualWages);
    }
  }

  public loadTaxesIndicators(profile: any, statesAndIncomeKeys: any): Observable<any> {

    let load = false;
    let statesData: any = {};

    for (let state in statesAndIncomeKeys) {

      for (let incomeKey of statesAndIncomeKeys[state]) {

        let cacheKey = "taxes:" + state + ":" + incomeKey;
        let taxes = this.cache.get(cacheKey);

        if (!taxes) {

          if (!statesData[state]) {
            statesData[state] = [];
          }

          statesData[state].push(incomeKey);
          load = true;
        }
      }
    }

    if (load) {
      return this.taxesUtils.loadTaxes(statesData).map((taxes: any[]) => {
        for (let item of taxes) {
          let cacheKey = "taxes:" + item.state + ":" + item.income_key;
          this.cache.set(cacheKey, item);
        }
      });
    } else {
      return Observable.of("");
    }
  }

  public calculateAffordability(profile, features): Observable<any> {

    let statesAndIncomeKeys: any = this.getStatesAndIncomeKeys(profile, features);
    return this.loadTaxesIndicators(profile, statesAndIncomeKeys).map(() => {
      for (let feature of features) {
        this.calculateIndicators(profile, feature);
      }
    });
  }
}




// WEBPACK FOOTER //
// ./common/utils/affordabilityutils.ts